import { useEffect, useRef, useState, type RefObject } from "react";

import { agentClient } from "../agentClient";

/** The textarea as this tab's own input last left it: remote changes are mapped from here. */
export interface Seen {
  text: string;
  start: number;
  end: number;
}

export interface LocalCaret {
  focused: boolean;
  caret: number;
}

const INPUT_EVENTS = ["input", "select", "focus", "keyup", "pointerup"] as const;

/**
 * Follows this tab's caret and selection in the composer and reports them as presence (the client
 * throttles). Blur keeps the last position: switching browser tabs blurs the textarea, and that is
 * when the other tab looks at it. The composer leaving the screen (the lip closing), unmounting or
 * a thread switch clears it; coming back on screen reports it again.
 */
export function useLocalCaret(
  input: RefObject<HTMLTextAreaElement | null>,
  threadId: number | null
) {
  const seen = useRef<Seen | null>(null);
  // After a remote edit moved the caret: re-reports it without resetting idle, but only a caret this
  // tab has reported (a tab never focused on this thread has none to move).
  const reread = useRef(() => {});
  const [local, setLocal] = useState<LocalCaret>({ focused: false, caret: 0 });

  useEffect(() => {
    const element = input.current;
    if (!element || threadId == null) return;
    let sent = "";
    let cleared = false;

    const read = (active: boolean) => {
      const { value, selectionStart: start, selectionEnd: end } = element;
      seen.current = { text: value, start, end };
      const caret = element.selectionDirection === "backward" ? start : end;
      const focused = document.activeElement === element;
      setLocal((last) =>
        last.focused === focused && last.caret === caret ? last : { focused, caret }
      );
      const selection: [number, number] | null = start < end ? [start, end] : null;
      const key = `${caret}:${selection}`;
      if (key === sent || cleared) return;
      sent = key;
      agentClient.reportPresence({ thread: threadId, caret, selection, active });
    };
    // Only while focused: setSelectionRange (a remote edit's re-mapping) fires select on a blurred one.
    const onInput = () => {
      if (document.activeElement === element) read(true);
    };
    reread.current = () => {
      if (sent) read(false);
    };
    const clear = () => {
      sent = "";
      agentClient.reportPresence({ caret: null, selection: null });
    };
    const visibility = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && sent) {
        clear();
        cleared = true;
      } else if (entry.isIntersecting && cleared) {
        cleared = false;
        read(false);
      }
    });
    const onBlur = () => setLocal((last) => (last.focused ? { ...last, focused: false } : last));

    for (const type of INPUT_EVENTS) element.addEventListener(type, onInput);
    element.addEventListener("blur", onBlur);
    document.addEventListener("selectionchange", onInput);
    visibility.observe(element);
    if (document.activeElement === element) read(true);
    return () => {
      reread.current = () => {};
      for (const type of INPUT_EVENTS) element.removeEventListener(type, onInput);
      element.removeEventListener("blur", onBlur);
      document.removeEventListener("selectionchange", onInput);
      visibility.disconnect();
      clear();
    };
  }, [input, threadId]);

  return { seen, local, reread };
}
